import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { 
  IoAddOutline, 
  IoTrophyOutline, 
  IoFilterOutline,
  IoSearchOutline,
  IoSparklesOutline
} from 'react-icons/io5';
import { useAchievements } from '../../hooks/useAchievements';
import AchievementCard from './Achievements/AchievementCard';
import STARBuilder from './Achievements/STARBuilder';
import { Achievement } from '../../hooks/useAchievements';

interface AchievementsTabProps {
  focus: any;
  onQuickAdd?: (context?: any) => void;
}

const AchievementsTab: React.FC<AchievementsTabProps> = ({ focus, onQuickAdd }) => {
  const { darkMode } = useDarkMode();
  const {
    achievements,
    featuredAchievements,
    loading,
    error,
    createAchievement,
    updateAchievement,
    deleteAchievement,
    toggleFeatured,
    refreshAchievements
  } = useAchievements(focus?.id);

  const [showSTARBuilder, setShowSTARBuilder] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showOnlyFeatured, setShowOnlyFeatured] = useState(false);

  // Style classes
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-700' : 'bg-white';

  // Filter achievements
  const filteredAchievements = achievements.filter(achievement => {
    const matchesSearch = searchQuery === '' || 
      achievement.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      achievement.situation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      achievement.result.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || achievement.category === filterCategory;
    const matchesFeatured = !showOnlyFeatured || achievement.is_featured;

    return matchesSearch && matchesCategory && matchesFeatured;
  });

  const handleSaveAchievement = async (achievementData: Omit<Achievement, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'focus_id'>) => {
    if (editingAchievement) {
      await updateAchievement(editingAchievement.id, achievementData);
    } else {
      await createAchievement({
        ...achievementData,
        focus_id: focus.id
      });
    }
    setShowSTARBuilder(false);
    setEditingAchievement(undefined);
  };

  const handleDeleteAchievement = async (id: string) => {
    if (confirm('Are you sure you want to delete this achievement?')) {
      await deleteAchievement(id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        key="achievements"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        {/* Header with Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h3 className={`text-lg font-medium ${textColor}`}>Achievement Stories</h3>
            <p className={`text-sm ${mutedTextColor} mt-1`}>
              Document your key achievements using the STAR method
            </p>
          </div>
          <button
            onClick={() => {
              setEditingAchievement(undefined);
              setShowSTARBuilder(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <IoAddOutline className="w-5 h-5" />
            <span>Add Achievement</span>
          </button>
        </div>

        {/* Filters and Search */}
        <div className={`mb-6 p-4 rounded-lg ${cardBg} border ${borderColor}`}>
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <IoSearchOutline className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${mutedTextColor}`} />
                <input
                  type="text"
                  placeholder="Search achievements..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                />
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className={`px-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
            >
              <option value="all">All Categories</option>
              <option value="technical">Technical</option>
              <option value="leadership">Leadership</option>
              <option value="collaboration">Collaboration</option>
              <option value="innovation">Innovation</option>
              <option value="impact">Business Impact</option>
              <option value="growth">Personal Growth</option>
            </select>

            {/* Featured Toggle */}
            <button
              onClick={() => setShowOnlyFeatured(!showOnlyFeatured)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                showOnlyFeatured 
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700'
                  : `${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700`
              } transition-colors`}
            >
              <IoTrophyOutline className="w-5 h-5" />
              <span>Featured Only</span>
            </button>
          </div>
        </div>

        {/* Featured Achievements Summary */}
        {featuredAchievements.length > 0 && !showOnlyFeatured && (
          <div className={`mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800`}>
            <div className="flex items-center gap-2 mb-2">
              <IoTrophyOutline className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <h4 className={`font-medium text-yellow-800 dark:text-yellow-200`}>
                Featured Achievements ({featuredAchievements.length}/5)
              </h4>
            </div>
            <p className={`text-sm text-yellow-700 dark:text-yellow-300`}>
              These achievements will be highlighted in your performance review. You can feature up to 5 achievements.
            </p>
          </div>
        )}

        {/* Achievements List */}
        {filteredAchievements.length > 0 ? (
          <div className="space-y-4">
            <AnimatePresence>
              {filteredAchievements.map((achievement) => (
                <AchievementCard
                  key={achievement.id}
                  achievement={achievement}
                  onEdit={() => {
                    setEditingAchievement(achievement);
                    setShowSTARBuilder(true);
                  }}
                  onDelete={() => handleDeleteAchievement(achievement.id)}
                  onToggleFeatured={() => toggleFeatured(achievement.id)}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className={`text-center py-12 ${cardBg} border ${borderColor} rounded-lg`}>
            <IoSparklesOutline className={`w-12 h-12 ${mutedTextColor} mx-auto mb-4`} />
            <p className={`${textColor} mb-2`}>
              {searchQuery || filterCategory !== 'all' || showOnlyFeatured
                ? 'No achievements match your filters'
                : 'No achievements documented yet'}
            </p>
            <p className={`text-sm ${mutedTextColor} mb-4`}>
              {searchQuery || filterCategory !== 'all' || showOnlyFeatured
                ? 'Try adjusting your search or filters'
                : 'Start building your achievement portfolio with STAR stories'}
            </p>
            {!searchQuery && filterCategory === 'all' && !showOnlyFeatured && (
              <button
                onClick={() => {
                  setEditingAchievement(undefined);
                  setShowSTARBuilder(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <IoAddOutline className="w-5 h-5" />
                <span>Create Your First Achievement</span>
              </button>
            )}
          </div>
        )}

        {/* Quick Tips */}
        <div className={`mt-6 p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800`}>
          <h4 className={`font-medium text-purple-800 dark:text-purple-200 mb-2`}>
            Tips for Great Achievement Stories
          </h4>
          <ul className={`text-sm text-purple-700 dark:text-purple-300 space-y-1`}>
            <li>• Use specific metrics and numbers in your results</li>
            <li>• Focus on achievements from the current review period</li>
            <li>• Include cross-functional collaboration examples</li>
            <li>• Highlight innovative solutions you implemented</li>
          </ul>
        </div>
      </motion.div>

      {/* STAR Builder Modal */}
      <STARBuilder
        isOpen={showSTARBuilder}
        onClose={() => {
          setShowSTARBuilder(false);
          setEditingAchievement(undefined);
        }}
        onSave={handleSaveAchievement}
        focusId={focus.id}
        existingAchievement={editingAchievement}
      />
    </>
  );
};

export default AchievementsTab;